import {
  makeStyles,
  Menu,
  MenuGroup,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components'
import { DatabaseRegular, DesktopRegular } from '@fluentui/react-icons'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useUploadFiles } from '../../hooks'
import { SUPPORTED_FILE_TYPES } from '../../types'

export interface FileSourceItem {
  id: string
  icon: React.ReactElement
  title: string
  onClick: () => void
}

interface FileUploadMenuProps {
  triggerButton: React.ReactElement
  openBraveSearch: () => void
}

const useStyles = makeStyles({
  hiddenForm: {
    display: 'none',
  },
})

const FileUploadMenu = ({ triggerButton, openBraveSearch }: FileUploadMenuProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { uploadFiles } = useUploadFiles()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const items: FileSourceItem[] = useMemo(
    () => [
      {
        id: 'local',
        icon: <DesktopRegular />,
        title: t('files.addLocalFileFromMenu'),
        onClick: openFilePicker,
      },
      {
        id: 'brave',
        icon: <DatabaseRegular />,
        title: t('brave.addFileFromMenu'),
        onClick: openBraveSearch,
      },
    ],
    [t, openFilePicker, openBraveSearch]
  )

  function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const uploadedFiles = event.target.files
    if (uploadedFiles?.length) {
      uploadFiles(uploadedFiles)
    } else {
      console.error('No file available for upload.')
    }
    // Reset so re-selecting the same file(s) fires onChange again
    event.target.value = ''
  }

  return (
    <>
      <Menu positioning={{ align: 'end', offset: { mainAxis: 4, crossAxis: -40 } }}>
        <MenuTrigger>{triggerButton}</MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuGroup>
              {items.map(({ icon, id, onClick, title }) => (
                <MenuItem icon={icon} key={id} onClick={onClick}>
                  {title}
                </MenuItem>
              ))}
            </MenuGroup>
          </MenuList>
        </MenuPopover>
      </Menu>
      <form className={classes.hiddenForm} aria-hidden="true">
        <label htmlFor="file-upload">{t('files.uploadFile')}</label>
        <input
          id="file-upload"
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          accept={SUPPORTED_FILE_TYPES.map((ext) => `.${ext}`).join(',')}
          multiple
        />
      </form>
    </>
  )
}

export { FileUploadMenu }
